<template>
	<section
		class="split-section"
		:class="[
			`split-section--image-${imageSide}`,
			`split-section--fit-${imageFit}`,
			{
				'has-image': imageSrc,
				'split-section--mobile-image-last': mobileImageLast,
				'split-section--has-aspect': visualAspectRatio,
				'split-section--visual-in-container': visualInContainer,
			},
		]"
		:style="sectionVars"
	>
		<div class="split-section__visual">
			<img
				v-if="imageSrc"
				class="split-section__image"
				:src="imageSrc"
				:alt="imageAlt"
				loading="lazy"
			/>
		</div>

		<div class="split-section__content">
			<h2
				v-if="title"
				class="split-section__title headline-l"
				v-html="title"
			></h2>

			<div
				v-if="body"
				class="split-section__body text-l"
				v-html="body"
			></div>

			<div v-if="$slots.footer" class="split-section__footer">
				<slot name="footer" />
			</div>
		</div>
	</section>
</template>

<script>
export default {
	name: 'SplitSection',
	props: {
		title: { type: String, default: '' },
		// rendered with v-html: bullet lists and <b> emphasis come from the i18n string
		body: { type: String, default: '' },
		imageSide: {
			type: String,
			default: 'right',
			validator: (v) => ['left', 'right'].includes(v),
		},
		// while the artwork is missing, the visual half falls back to a flat panelColor
		// block -- transparent by default so nothing but the section background shows
		// through around a 'contain' image
		imageSrc: { type: String, default: '' },
		imageAlt: { type: String, default: '' },
		panelColor: { type: String, default: 'transparent' },
		// 'cover' fills the half (right for full-bleed renders), 'contain' keeps a
		// diagram whole
		imageFit: {
			type: String,
			default: 'cover',
			validator: (v) => ['cover', 'contain'].includes(v),
		},
		backgroundColor: { type: String, default: '#E8E8FF' },
		// decorative artwork behind the whole section
		backgroundImage: { type: String, default: '' },
		// backdrops built for other sections can overpower the text at this size
		backgroundOpacity: { type: [Number, String], default: 1 },
		titleColor: { type: String, default: '#050535' },
		textColor: { type: String, default: '#262A52' },
		minHeight: { type: [Number, String], default: 480 },
		// hairline separating this section from the one above it
		borderTop: { type: String, default: 'none' },
		borderBottom: { type: String, default: 'none' },
		// Gap between the text and the artwork, as a share of the visual's own half.
		// The default 15% of a half == 7.5% of the page, which puts the artwork edge
		// at 57.5% -- matching the mockup, where the artwork is 42.5% wide.
		visualInset: { type: String, default: '15%' },
		// breathing room around a contained diagram
		visualPadding: { type: String, default: '0' },
		// object-position for the artwork. A 'contain' render that is meant to run
		// off the bottom edge needs 'center bottom': once the copy gets tall
		// enough that the width becomes the limiting side, a centred image floats
		// with a gap under it instead of sitting on the section edge.
		imagePosition: { type: String, default: 'center' },
		// Stacked order on mobile is title -> image -> body, per the mockup. The
		// closing section is the exception: there the artwork follows the buttons.
		mobileImageLast: { type: Boolean, default: false },
		// caps a 'contain' image once it goes full width; the phone render would
		// otherwise fill the screen where the mockup keeps it about half that
		mobileImageMaxWidth: { type: String, default: 'none' },
		// artwork behind imageSrc, filling the visual half. Lets a diagram that
		// was exported without a backdrop sit on its own scenery.
		visualBackgroundImage: { type: String, default: '' },
		// The image's own ratio, e.g. '1830 / 1146'. Gives the visual half a
		// height of its own instead of the row's, so a section whose copy is
		// shorter than the artwork takes the artwork's height and the image fits
		// exactly -- no band of section background above or below it.
		visualAspectRatio: { type: String, default: '' },
		// Stops the artwork at the container edge instead of letting it bleed to
		// the viewport edge, so it lines up with the gutter the sections keep on
		// the other side.
		visualInContainer: { type: Boolean, default: false },
		// Vertical breathing room around the copy. Worth trimming on a section
		// with visualAspectRatio: the shorter the copy column, the more likely the
		// artwork is the taller half and gets shown uncropped.
		contentPaddingY: { type: String, default: '90px' },
	},
	components: {},
	data: () => ({}),
	created() {},
	computed: {
		sectionVars() {
			return {
				'--bg': this.backgroundColor,
				'--panel': this.panelColor,
				'--title-color': this.titleColor,
				'--text-color': this.textColor,
				'--visual-inset': this.visualInset,
				'--image-fit': this.imageFit,
				'--image-position': this.imagePosition,
				'--visual-padding': this.visualPadding,
				'--mobile-image-max-width': this.mobileImageMaxWidth,
				'--visual-bg-image': this.visualBackgroundImage
					? `url(${this.visualBackgroundImage})`
					: 'none',
				'--visual-aspect': this.visualAspectRatio || 'auto',
				'--content-padding-y': this.contentPaddingY,
				'--border-top': this.borderTop,
				'--border-bottom': this.borderBottom,
				'--bg-image': this.backgroundImage
					? `url(${this.backgroundImage})`
					: 'none',
				'--bg-opacity': this.backgroundOpacity,
				'--min-height':
					typeof this.minHeight === 'number'
						? `${this.minHeight}px`
						: this.minHeight,
			};
		},
	},
	methods: {},
};
</script>

<style lang="scss" scoped>
.split-section {
	position: relative;
	background-color: var(--bg);
	border-top: var(--border-top);
	border-bottom: var(--border-bottom);
	display: grid;
	grid-template-columns: 1fr 1fr;
	align-items: stretch;

	// Stacked, and centred like the rest of the site's mobile sections. Flex
	// rather than a one-column grid so `order` can interleave the artwork
	// between the title and the body -- see .split-section__content below.
	@media (max-width: $breakpoint-sm) {
		display: flex;
		flex-direction: column;
		padding: 50px 0;
		text-align: center;
	}

	&::before {
		content: '';
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		background-image: var(--bg-image);
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		opacity: var(--bg-opacity);
		pointer-events: none;
	}
}

.split-section__visual,
.split-section__content {
	position: relative;
	z-index: 1;
}

.split-section__visual {
	position: relative;
	// the image is absolutely positioned, so visualPadding is applied via its
	// offsets rather than here
	min-height: var(--min-height);
	background-color: var(--panel);
	background-image: var(--visual-bg-image);
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	overflow: hidden;

	@media (max-width: $breakpoint-sm) {
		// full-bleed band under the title, no side gutters
		min-height: 260px;
		margin: 0;
		order: 2;
	}
}

// aspect-ratio suppresses the grid's cross-axis stretch, so the half sizes
// itself from the artwork. Pinned to the bottom of the row: where the copy is
// shorter the row takes the artwork's height and the image sits flush on both
// edges; where the copy is taller the leftover gap lands above the image,
// which is how the mockup has it.
.split-section--has-aspect .split-section__visual {
	min-height: 0;
	aspect-ratio: var(--visual-aspect);
	align-self: end;

	// Below the desktop breakpoint the copy column is narrow enough that it runs
	// far taller than the artwork, and pinning to the bottom leaves the image
	// sitting in the corner. Centre it in that band instead.
	@media (min-width: 768px) and (max-width: $breakpoint-lg) {
		align-self: center;
	}

	@media (max-width: $breakpoint-sm) {
		min-height: 0;
		// stacked, align-self is the horizontal axis: `end` there shrinks the
		// half to its content, and an absolutely positioned image contributes
		// none, so the artwork disappears
		align-self: stretch;
	}
}

// A diagram has no useful crop, so on mobile it drops out of the fixed-height
// band and just runs at its natural aspect across the full width.
.split-section--fit-contain .split-section__visual {
	@media (max-width: $breakpoint-sm) {
		min-height: 0;

		.split-section__image {
			position: static;
			width: 100%;
			height: auto;
			max-width: var(--mobile-image-max-width);
			margin: 0 auto;
			display: block;
		}
	}
}

.split-section--image-right .split-section__visual {
	@media (min-width: 768px) {
		margin-left: var(--visual-inset);
	}
}

.split-section--image-left .split-section__visual {
	@media (min-width: 768px) {
		margin-right: var(--visual-inset);
	}
}

// Mirror of the copy column's max-width/padding, so the outer edge of the
// artwork sits on the same line as the container's gutter.
.split-section--visual-in-container {
	&.split-section--image-right .split-section__visual {
		@media (min-width: 768px) {
			margin-right: calc(100% - 345px);
		}
		@media (min-width: 992px) {
			margin-right: calc(100% - 465px);
		}
		@media (min-width: 1200px) {
			margin-right: calc(100% - 555px);
		}
	}

	&.split-section--image-left .split-section__visual {
		@media (min-width: 768px) {
			margin-left: calc(100% - 345px);
		}
		@media (min-width: 992px) {
			margin-left: calc(100% - 465px);
		}
		@media (min-width: 1200px) {
			margin-left: calc(100% - 555px);
		}
	}
}

.split-section--image-right {
	.split-section__visual {
		grid-column: 2;
		grid-row: 1;
	}

	.split-section__content {
		grid-column: 1;
		grid-row: 1;
	}

	@media (max-width: $breakpoint-sm) {
		.split-section__visual,
		.split-section__content {
			grid-column: 1;
			grid-row: auto;
		}
	}
}

// The artwork bleeds to the viewport edge, so it covers its half entirely.
// Sized by all four offsets rather than width/height: calc(100% - var(...) * 2)
// is invalid at computed-value time when the padding is a bare 0, which drops
// the image back to width/height: auto -- i.e. its full natural size.
.split-section__image {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	// visualPadding as padding on the replaced element itself: object-fit sizes
	// the artwork inside the content box, so the inset costs nothing in layout.
	// calc(100% - var(--visual-padding) * 2) cannot be used -- with a bare 0 it
	// is invalid at computed-value time and width falls back to auto, i.e. the
	// image's full natural size.
	padding: var(--visual-padding);
	box-sizing: border-box;
	object-fit: var(--image-fit);
	object-position: var(--image-position);

	// the half is narrow enough on a tablet without giving away more to padding
	@media (max-width: $breakpoint-md) {
		padding: 0;
	}
}

// Each half is exactly 50% of the page, so capping the text block at half the
// Bootstrap container width and pushing it against the page centre lines it up
// with .container content -- without 100vw maths, which would be off by the
// scrollbar width.
.split-section__content {
	display: flex;
	flex-direction: column;
	justify-content: center;
	// no padding on the inner edge: the text runs to the page centre line, so it
	// gets the full container half (the gutter is added on the outer edge below)
	padding: var(--content-padding-y) 0;

	@media (min-width: 768px) {
		max-width: 360px;
	}
	@media (min-width: 992px) {
		max-width: 480px;
	}
	@media (min-width: 1200px) {
		max-width: 570px;
	}

	// display: contents lifts the title, body and footer out to become flex
	// children of the section itself, which is what lets the artwork sit between
	// the title and the body. The wrapper's padding goes with it, so the gutter
	// moves onto each child (the artwork stays full-bleed).
	@media (max-width: $breakpoint-sm) {
		display: contents;
	}
}

.split-section--image-right .split-section__content {
	margin-left: auto;
	padding-left: 15px;
}

.split-section--image-left .split-section__content {
	margin-right: auto;
	padding-right: 15px;
}

.split-section__title {
	color: var(--title-color);
	margin-bottom: 24px;
}

.split-section__body {
	color: var(--text-color);

	&::v-deep {
		p {
			margin-bottom: 16px;

			&:last-child {
				margin-bottom: 0;
			}
		}

		b,
		strong {
			font-family: 'HKNova-Bold', sans-serif;
		}

		ul {
			list-style: disc;
			padding-left: 20px;
			margin: 0 0 16px;
		}

		li {
			margin-bottom: 4px;
		}

		a {
			color: inherit;
			text-decoration: underline;
		}
	}
}

.split-section__footer {
	margin-top: 28px;
}

// Stacking order and gutters for the flattened mobile layout. Kept last so it
// wins over the base rules above -- a media query adds no specificity.
@media (max-width: $breakpoint-sm) {
	// display: contents removes the wrapper's box, and with it the stacking
	// context that kept the decorative ::before layer behind the copy
	.split-section__title,
	.split-section__body,
	.split-section__footer {
		position: relative;
		z-index: 1;
	}

	.split-section__title {
		order: 1;
		padding: 0 15px;
		margin-bottom: 30px;
	}

	.split-section__body {
		order: 3;
		padding: 0 15px;
		margin-top: 30px;

		// centred copy: a hanging bullet would sit far off to the left
		&::v-deep ul {
			list-style-position: inside;
			padding-left: 0;
		}
	}

	.split-section__footer {
		order: 4;
		padding: 0 15px;
		margin-top: 28px;
	}

	.split-section--mobile-image-last {
		// the artwork runs to the section edge, so the bottom padding goes
		padding-bottom: 0;

		.split-section__visual {
			order: 4;
			margin-top: 30px;
		}

		.split-section__body {
			order: 2;
			margin-top: 0;
		}

		.split-section__footer {
			order: 3;
		}
	}
}
</style>
